import {
  FileTextOutlined,
  InboxOutlined,
  UserAddOutlined
} from "@ant-design/icons"
import { Alert, Form, Modal, Select, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { BulkActionBar } from "../../../../components/Table/BulkActionBar.tsx"
import type { ProjectBulkResult } from "../../redux/projects.types.ts"

const { Text } = Typography

export type ProjectBulkAction = "change-status" | "assign-pm" | "archive"

export interface ProjectBulkActionParams {
  action: ProjectBulkAction
  ids: string[]
  status?: number
  projectManagerId?: string
}

interface Option<V> {
  label: string
  value: V
}

interface ProjectsBulkActionBarProps {
  selectedIds: string[]
  // A selection survives loading more rows, so a failure can name a project
  // whose row is not currently mounted — fall back to the id when it is absent.
  projectNames: Record<string, string>
  statusOptions: Option<number>[]
  projectManagerOptions: Option<string>[]
  editAllowed?: boolean
  assignPmAllowed?: boolean
  archiveAllowed?: boolean
  onClear: () => void
  onAction: (params: ProjectBulkActionParams) => Promise<ProjectBulkResult>
  /**
   * The bar outlives the selection — it keeps reporting the outcome after an
   * action clears it — so anything anchored to the same corner follows this
   * rather than the selection.
   */
  onVisibleChange?: (visible: boolean) => void
}

type BulkFormValues = { status?: number; projectManagerId?: string }

export function ProjectsBulkResult({
  result,
  projectNames
}: {
  result: ProjectBulkResult
  projectNames: Record<string, string>
}) {
  const { t } = useTranslation()

  return (
    <div>
      <Text strong>
        {t("projectBulk.result.summary", {
          succeeded: result.succeeded.length,
          requested: result.requested
        })}
      </Text>
      {/* Bounded because the bar is pinned to the bottom: an unbounded list
          grows upwards over the table rather than overflowing down. */}
      {result.failed.length ? (
        <ul
          data-testid="projects-bulk-result-failures"
          // Nothing inside takes focus, so the region carries it — otherwise
          // the refusals below the cap are unreachable without a pointer.
          tabIndex={0}
          aria-label={t("projectBulk.result.failuresLabel", {
            count: result.failed.length
          })}
          className="mb-0 mt-2 max-h-[30vh] list-disc overflow-y-auto pl-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A7D8C]"
        >
          {result.failed.map((failure) => (
            <li key={failure.id}>
              {t("projectBulk.result.failure", {
                project: projectNames[failure.id] || failure.id,
                reason: failure.message
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function ProjectsBulkActionBar({
  selectedIds,
  projectNames,
  statusOptions,
  projectManagerOptions,
  editAllowed,
  assignPmAllowed,
  archiveAllowed,
  onClear,
  onAction,
  onVisibleChange
}: ProjectsBulkActionBarProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<BulkFormValues>()
  const [activeAction, setActiveAction] = useState<ProjectBulkAction | null>(
    null
  )
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProjectBulkResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)

  const actionLabels = useMemo<Record<ProjectBulkAction, string>>(
    () => ({
      "change-status": t("projectBulk.actions.changeStatus"),
      "assign-pm": t("projectBulk.actions.assignPm"),
      archive: t("projectBulk.actions.archive")
    }),
    [t]
  )

  const closeModal = () => {
    if (submitting) return
    setActiveAction(null)
    form.resetFields()
  }

  const openModal = (action: ProjectBulkAction) => {
    form.resetFields()
    setActiveAction(action)
  }

  const submitAction = async () => {
    if (!activeAction) return

    let values: BulkFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    // The field's own rule covers this, but the request must not depend on the
    // form being connected in time.
    if (activeAction === "change-status" && values.status == null) return
    if (activeAction === "assign-pm" && !values.projectManagerId) return

    setSubmitting(true)
    try {
      const nextResult = await onAction({
        action: activeAction,
        ids: selectedIds,
        ...(activeAction === "change-status" ? { status: values.status } : {}),
        ...(activeAction === "assign-pm"
          ? { projectManagerId: values.projectManagerId }
          : {})
      })
      setRequestFailed(false)
      setResult(nextResult)
      setActiveAction(null)
      form.resetFields()
    } catch {
      setResult(null)
      setRequestFailed(true)
      setActiveAction(null)
      form.resetFields()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    onClear()
  }

  const visible = !!selectedIds.length || !!result || requestFailed

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  if (!visible) return null

  const feedback =
    requestFailed || result ? (
      <>
        {requestFailed ? (
          <Alert
            data-testid="projects-bulk-request-error"
            type="error"
            showIcon
            closable
            onClose={() => setRequestFailed(false)}
            message={t("projectBulk.result.requestFailed")}
          />
        ) : null}

        {result ? (
          <Alert
            type={result.failed.length ? "warning" : "success"}
            showIcon
            closable
            onClose={() => setResult(null)}
            message={
              <ProjectsBulkResult result={result} projectNames={projectNames} />
            }
          />
        ) : null}
      </>
    ) : null

  return (
    <>
      <BulkActionBar
        data-testid="projects-bulk-action-bar"
        visible
        selectedCount={selectedIds.length}
        selectedLabel={t("projectBulk.selected", { count: selectedIds.length })}
        ariaLabel={t("projectBulk.toolbarLabel")}
        actions={[
          ...(editAllowed
            ? [
                {
                  key: "change-status",
                  label: actionLabels["change-status"],
                  icon: <FileTextOutlined aria-hidden />,
                  onClick: () => openModal("change-status")
                }
              ]
            : []),
          ...(assignPmAllowed
            ? [
                {
                  key: "assign-pm",
                  label: actionLabels["assign-pm"],
                  icon: <UserAddOutlined aria-hidden />,
                  onClick: () => openModal("assign-pm")
                }
              ]
            : [])
        ]}
        destructiveAction={
          archiveAllowed
            ? {
                key: "archive",
                label: actionLabels.archive,
                icon: <InboxOutlined aria-hidden />,
                onClick: () => openModal("archive")
              }
            : undefined
        }
        onClear={clear}
      >
        {/* Below the toolbar, so a result does not push the controls still in
            use further down the page. */}
        {feedback}
      </BulkActionBar>

      <Modal
        open={activeAction != null}
        title={activeAction ? actionLabels[activeAction] : undefined}
        okText={
          activeAction === "archive"
            ? t("projectBulk.actions.archive")
            : t("projectBulk.apply")
        }
        cancelText={t("projectBulk.cancel")}
        okButtonProps={{ danger: activeAction === "archive" }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        maskClosable={false}
        destroyOnHidden
        onOk={submitAction}
        onCancel={closeModal}
      >
        <Form form={form} layout="vertical" disabled={submitting}>
          {activeAction === "change-status" ? (
            <Form.Item<BulkFormValues>
              name="status"
              label={t("projectBulk.fields.status")}
              rules={[
                {
                  required: true,
                  message: t("projectBulk.validation.status")
                }
              ]}
            >
              <Select
                options={statusOptions}
                placeholder={t("projectBulk.placeholders.status")}
              />
            </Form.Item>
          ) : null}

          {activeAction === "assign-pm" ? (
            <>
              <Form.Item<BulkFormValues>
                name="projectManagerId"
                label={t("projectBulk.fields.projectManager")}
                rules={[
                  {
                    required: true,
                    message: t("projectBulk.validation.projectManager")
                  }
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={projectManagerOptions}
                  placeholder={t("projectBulk.placeholders.projectManager")}
                />
              </Form.Item>
              <Text type="secondary">{t("projectBulk.assignPmHint")}</Text>
            </>
          ) : null}

          {activeAction === "archive" ? (
            <Alert
              type="error"
              showIcon
              message={t("projectBulk.archiveImpact", {
                count: selectedIds.length
              })}
            />
          ) : null}
        </Form>
      </Modal>
    </>
  )
}
